import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { SESSION_TIMEOUT_MS } from '@/config/constants';
import { DICTIONARY } from '@/config/dictionary';
import { APP_PATHS } from '@/config/routes/app-paths';
import { useAppStore } from '@/stores/app-store';
import { ensureValidPath } from '@/utils/validate-path';

export function useSessionTimeout() {
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const sessionStartTime = useAppStore((s) => s.sessionStartTime);
  const clearSession = useAppStore((s) => s.clearSession);
  const addNotification = useAppStore((s) => s.addNotification);
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isAuthenticated) return;

    const expire = () => {
      clearSession();
      void navigate({ to: ensureValidPath(APP_PATHS.home), search: { tab: 'signin' } });
      addNotification({
        type: 'warning',
        title: DICTIONARY.sessionTimeout.toastTitle,
        message: DICTIONARY.sessionTimeout.toastDescription,
      });
    };

    // Authenticated with no start stamp is an unbounded session — the timer below can never be
    // armed, so it would last until the user signed out by hand. It only reaches us out of the
    // persisted store (`pingco-auth` in localStorage survives a browser restart, the access token
    // in sessionStorage does not), written by a sign-in path that set `isAuthenticated` without
    // calling `startSession`. Treat it as already expired rather than as immortal.
    //
    // Not a race with a live sign-in: every caller invokes `startSession()` synchronously
    // alongside `setIsAuthenticated(true)`, and React batches both into one render, so this effect
    // never observes the half-set state in between.
    if (sessionStartTime === null) {
      expire();
      return;
    }

    const elapsed = Date.now() - sessionStartTime;
    const remaining = SESSION_TIMEOUT_MS - elapsed;

    if (remaining <= 0) {
      // Already expired (e.g. user refreshed the page after timeout)
      expire();
      return;
    }

    timeoutRef.current = setTimeout(expire, remaining);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, sessionStartTime, clearSession, addNotification, navigate]);
}
